import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, Lock, DollarSign, Clock, CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientSecurityPage = () => {
  const { getAuthHeaders } = useAuth();
  const [hasPin, setHasPin] = useState(false);
  const [pin, setPin] = useState('');
  const [limits, setLimits] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const h = { headers: getAuthHeaders() };
        const [pinRes, limRes, auditRes] = await Promise.all([
          axios.get(`${API}/client/security/has-pin`, h),
          axios.get(`${API}/client/limits`, h),
          axios.get(`${API}/client/audit-log?limit=20`, h),
        ]);
        setHasPin(pinRes.data.has_pin);
        setLimits(limRes.data);
        setAuditLog(auditRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const savePin = async () => {
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      toast.error('El PIN debe ser de 4 digitos');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/client/security/set-pin`, { pin }, { headers: getAuthHeaders() });
      toast.success('PIN configurado exitosamente');
      setHasPin(true);
      setPin('');
    } catch (e) { toast.error('Error al configurar PIN'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  const actionLabels = {
    'REGISTRO': 'Cuenta creada',
    'LOGIN': 'Inicio de sesion',
    'PIN_CONFIGURADO': 'PIN actualizado',
    'PIN_FALLIDO': 'Intento PIN incorrecto',
    'CHAT_ENVIADO': 'Mensaje de chat',
    'RECIBO_DESCARGADO': 'Recibo descargado',
  };

  return (
    <div className="space-y-6" data-testid="client-security-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Seguridad</h1>
        <p className="text-slate-500">Configura tu PIN y revisa tu actividad</p>
      </div>

      {/* PIN Setup */}
      <Card className="p-6 border-0 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
            <Lock className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">PIN de seguridad</h3>
            <p className="text-sm text-slate-500">{hasPin ? 'PIN activo. Puedes cambiarlo.' : 'Configura un PIN de 4 digitos para operaciones sensibles.'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Input type="password" maxLength={4} placeholder="1234" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} className="w-32 text-center tracking-[0.5em] font-mono text-lg" data-testid="pin-input" />
          <Button onClick={savePin} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="save-pin-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (hasPin ? 'Cambiar PIN' : 'Establecer PIN')}
          </Button>
        </div>
        {hasPin && (
          <div className="flex items-center gap-2 mt-3 text-green-600 text-sm">
            <CheckCircle className="w-4 h-4" /> PIN activo
          </div>
        )}
      </Card>

      {/* Transaction Limits */}
      {limits && (
        <Card className="p-6 border-0 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Limites de transacciones</h3>
              <p className="text-sm text-slate-500">Limites configurados por el administrador</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-xl" data-testid="daily-limit">
              <p className="text-sm text-slate-500">Limite diario</p>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-2xl font-bold text-slate-900">${limits.daily_spent.toFixed(2)}</span>
                <span className="text-sm text-slate-400">/ ${limits.daily_limit.toFixed(2)}</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(100, (limits.daily_spent / limits.daily_limit) * 100)}%` }} />
              </div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl" data-testid="weekly-limit">
              <p className="text-sm text-slate-500">Limite semanal</p>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-2xl font-bold text-slate-900">${limits.weekly_spent.toFixed(2)}</span>
                <span className="text-sm text-slate-400">/ ${limits.weekly_limit.toFixed(2)}</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(100, (limits.weekly_spent / limits.weekly_limit) * 100)}%` }} />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Audit Log */}
      <Card className="p-6 border-0 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Clock className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Historial de actividad</h3>
            <p className="text-sm text-slate-500">Ultimas acciones en tu cuenta</p>
          </div>
        </div>
        {auditLog.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-4">Sin actividad registrada</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {auditLog.map((log) => (
              <div key={log.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg text-sm" data-testid={`audit-${log.id}`}>
                <div>
                  <p className="font-medium text-slate-800">{actionLabels[log.action] || log.action}</p>
                  {log.details && <p className="text-xs text-slate-500">{log.details}</p>}
                </div>
                <span className="text-xs text-slate-400 whitespace-nowrap ml-2">{new Date(log.created_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ClientSecurityPage;
